import { useEffect, useRef } from "react";

import { Text } from "@alpacahq/ui/old";

import { useGetOrderbooks } from "~/v2/api/hooks";
import type { Asset } from "~/v2/api/rest";

interface OrderbookProps {
  asset: Asset;
}

export const Orderbook = ({ asset }: OrderbookProps) => {
  // use a ref to prevent re-rendering
  const ready = useRef(false);
  const rows = 25;

  const { orderbooks } = useGetOrderbooks({ symbols: [asset?.symbol || ""] });
  const { a: asks, b: bids } = orderbooks?.[asset?.symbol || ""] || {
    a: [],
    b: [],
  };

  // largest ask size (base)
  const maxAskSize: number = asks
    .slice(0, rows)
    .reduce((a, b) => (a > b.s ? a : b.s), 0);

  // largest bid size (base)
  const maxBidSize: number = bids
    .slice(0, rows)
    .reduce((a, b) => (a > b.s ? a : b.s), 0);

  // this is the quoted volume (base * quote)
  const getBidVolumeAtIndex = (index: number) => {
    const bid = bids.slice(0, rows).at(index);
    return (bid?.p || 0) * (bid?.s || 0);
  };

  // this is the quoted volume (base * quote)
  const getAskVolumeAtIndex = (index: number) => {
    const ask = asks.slice(0, rows).reverse().at(index);
    return (ask?.p || 0) * (ask?.s || 0);
  };

  // what percent is this value of the max size
  const getPercentOfMaxBidSize = (value: number) =>
    Math.max(1, (Number(value) / maxBidSize) * 100);

  // what percent is this value of the max size
  const getPercentOfMaxAskSize = (value: number) =>
    Math.max(1, (Number(value) / maxAskSize) * 100);

  // price between spread
  const getMidPrice = () => {
    const bid = bids[0];
    const ask = asks[0];

    if (!(bid && ask)) {
      return 0;
    }

    const mid = (bid.p + ask.p) / 2;

    const event = new CustomEvent("price-update", {
      detail: {
        symbol: asset?.symbol,
        price: mid,
      },
    });

    window.dispatchEvent(event);
    return mid;
  };

  useEffect(() => {
    const align = () => {
      // center-align orderbook
      document.getElementById("mid-price")?.scrollIntoView({
        block: "center",
      });

      // scroll up to top of page
      document.getElementById("snapshot")?.scrollIntoView({
        block: "start",
      });
    };

    if (!ready.current) {
      if (asks.length > 0 && bids.length > 0) {
        ready.current = true;
        align();
      }
    }
  }, [asks, bids]);

  return (
    <div
      className="overflow-y-scroll whitespace-nowrap"
      style={{ overflowAnchor: "none" }}
    >
      <div className="font-medium text-sm">
        {asks
          .slice(0, rows)
          .reverse()
          .map((ask, index) => (
            <div
              className="flash--red mb-[0.1rem]"
              key={`ask-${ask.p}-${ask.s}`}
            >
              <div
                className="flex"
                style={{
                  background: `linear-gradient(90deg, var(--theme-banner-red-background) ${getPercentOfMaxAskSize(
                    ask.s,
                  )}%, transparent 0%)`,
                }}
              >
                <div className="ml-3 w-[30%] text-red-400">
                  {Number(ask.p).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 8,
                  })}
                </div>
                <div className="w-[30%] text-right opacity-60">
                  {ask.s.toLocaleString("en-US", {
                    minimumFractionDigits: 3,
                  })}
                </div>
                <div className="mr-3 grow text-right opacity-60">
                  {Intl.NumberFormat("en-US", {
                    notation: "compact",
                    maximumFractionDigits: 1,
                  }).format(getAskVolumeAtIndex(index))}
                </div>
              </div>
            </div>
          ))}
      </div>
      <Text className="my-2 ml-3 font-semibold text-lg" id="mid-price">
        {getMidPrice().toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 10,
        })}
      </Text>
      <div className="textr-sm font-medium">
        {bids.slice(0, rows).map((bid, index) => (
          <div
            className="flash--green mb-[0.1rem]"
            key={`bid-${bid.p}-${bid.s}`}
          >
            <div
              className="flex"
              style={{
                background: `linear-gradient(90deg, var(--theme-banner-emerald-background) ${getPercentOfMaxBidSize(
                  bid.s,
                )}%, transparent 0%)`,
              }}
            >
              <div className="ml-3 w-[30%] text-green-400">
                {Number(bid.p).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 8,
                })}
              </div>
              <div className="w-[30%] text-right opacity-60">
                {bid.s.toLocaleString("en-US", {
                  minimumFractionDigits: 3,
                })}
              </div>
              <div className="mr-3 grow text-right opacity-60">
                {Intl.NumberFormat("en-US", {
                  notation: "compact",
                  maximumFractionDigits: 1,
                }).format(getBidVolumeAtIndex(index))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
